import React from 'react';
import { DEMO_SAE_EVENTS, DEMO_SUSAR_CLASSIFICATIONS } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';

const SAE_GLOSSARY: GlossaryEntry[] = [
  { term: 'SAE', definition: 'Serious Adverse Event \u2014 meets one of: death, life threatening, hospitalization, disability, congenital anomaly' },
  { term: 'SUSAR', definition: 'Suspected Unexpected Serious Adverse Reaction \u2014 serious, drug-related, and not listed in the Investigator Brochure. Requires expedited reporting to FDA within 7 days (fatal/life-threatening) or 15 days (all others)' },
  { term: '21 CFR 312.32', definition: 'FDA regulation governing IND safety reporting requirements and timelines' },
  { term: '7-day report', definition: 'Fatal or life-threatening SAE must be reported to FDA within 7 calendar days' },
  { term: '15-day report', definition: 'All other SAEs must be reported to FDA within 15 calendar days' },
  { term: 'IB', definition: 'Investigator Brochure \u2014 document listing known adverse reactions for the compound' },
  { term: 'Expected SAE', definition: 'SAE whose nature and severity are listed in the current Investigator Brochure' },
  { term: 'Expedited reporting', definition: 'Accelerated notification to FDA, IRB, and all investigators running the compound' },
  { term: '50_pct / 80_pct', definition: 'Alert thresholds \u2014 percentage of reporting window elapsed' },
];

function getTimeRemaining(deadline: string): { hours: number; pct: number; color: string; label: string } {
  const now = new Date();
  const dl = new Date(deadline);
  const hoursLeft = (dl.getTime() - now.getTime()) / (1000 * 3600);
  // Use 7 days as reference window for percentage
  const totalHours = 7 * 24;
  const pct = Math.max(0, Math.min(100, (hoursLeft / totalHours) * 100));

  let color = '#48bb78'; // green
  let label = 'On track';
  if (pct < 20) {
    color = '#f56565';
    label = 'Critical';
  } else if (pct < 50) {
    color = '#ed8936';
    label = 'Warning';
  }
  if (hoursLeft <= 0) {
    color = '#c53030';
    label = 'OVERDUE';
  }

  return { hours: Math.max(0, hoursLeft), pct, color, label };
}

export default function SAEClock() {
  const totalSaes = DEMO_SAE_EVENTS.length;
  const susarCount = DEMO_SUSAR_CLASSIFICATIONS.filter((c) => c.classification === 'SUSAR').length;
  const expectedCount = totalSaes - susarCount;

  return (
    <div style={styles.container}>
      <Glossary entries={SAE_GLOSSARY} />
      <h2 style={styles.title}>SAE Compliance Clock</h2>
      <p style={styles.desc}>
        Active SAE reporting deadlines per 21 CFR 312.32. Color indicates time remaining:
        green (&gt;50%), amber (20-50%), red (&lt;20%).
      </p>
      <ComplianceBadges badges={["21 CFR 312.32", "ICH E6(R3)", "MHRA Apr 2026", "UK CTR 2025"]} />

      {/* SUSAR Summary */}
      <div style={styles.susarSummaryRow}>
        <div style={styles.susarCard}>
          <div style={styles.susarLabel}>Total SAEs</div>
          <div style={styles.susarValue}>{totalSaes}</div>
        </div>
        <div style={styles.susarCard}>
          <div style={styles.susarLabel}>Expected SAEs</div>
          <div style={{ ...styles.susarValue, color: '#48bb78' }}>{expectedCount}</div>
        </div>
        <div style={{ ...styles.susarCard, border: '1px solid #f56565' }}>
          <div style={styles.susarLabel}>SUSARs</div>
          <div style={{ ...styles.susarValue, color: '#f56565' }}>{susarCount}</div>
          <div style={{ fontSize: 10, color: '#fc8181', marginTop: 2 }}>Requires expedited reporting</div>
        </div>
      </div>

      <div style={styles.grid}>
        {DEMO_SAE_EVENTS.map((sae) => {
          const tr = getTimeRemaining(sae.deadline);
          return (
            <div key={sae.sae_id} style={{ ...styles.card, borderLeft: `4px solid ${tr.color}` }}>
              <div style={styles.cardTop}>
                <div>
                  <div style={styles.saeId}>{sae.sae_id}</div>
                  <div style={styles.patient}>Patient: {sae.patient_id}</div>
                </div>
                <div style={{ ...styles.typeBadge, backgroundColor: sae.report_type === '7day' ? '#742a2a' : '#744210', color: sae.report_type === '7day' ? '#fc8181' : '#ecc94b' }}>
                  {sae.report_type}
                </div>
              </div>
              <div style={styles.description}>{sae.description}</div>
              {(() => {
                const susar = DEMO_SUSAR_CLASSIFICATIONS.find((c) => c.saeId === sae.sae_id);
                if (!susar) return null;
                const isSusar = susar.classification === 'SUSAR';
                return (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '3px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        backgroundColor: isSusar ? '#742a2a' : '#22543d',
                        color: isSusar ? '#fc8181' : '#48bb78',
                      }}>
                        {susar.classification}
                      </span>
                      <span style={{ color: '#718096', fontSize: 11 }}>{susar.reportingWindow}</span>
                    </div>
                    <div style={{ color: '#a0aec0', fontSize: 11, marginTop: 4 }}>{susar.ibReference}</div>
                    {susar.requiresExpeditedNotification && (
                      <div style={{
                        marginTop: 6, backgroundColor: 'rgba(245, 101, 101, 0.1)',
                        border: '1px solid #742a2a', borderRadius: 6, padding: '6px 10px',
                        color: '#fc8181', fontSize: 11, fontWeight: 600,
                      }}>
                        FDA + all investigators must be notified
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={styles.countdown}>
                <div style={styles.hoursLeft}>
                  <span style={{ ...styles.hoursNum, color: tr.color }}>
                    {tr.hours > 0 ? tr.hours.toFixed(0) : '0'}
                  </span>
                  <span style={styles.hoursUnit}>hours left</span>
                </div>
                <span style={{ ...styles.statusBadge, backgroundColor: tr.color + '22', color: tr.color }}>
                  {tr.label}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${100 - tr.pct}%`, backgroundColor: tr.color }} />
              </div>
              <div style={styles.dates}>
                <span>SAE: {new Date(sae.sae_date).toLocaleDateString()}</span>
                <span>Deadline: {new Date(sae.deadline).toLocaleDateString()}</span>
              </div>
              {sae.alerts.length > 0 && (
                <div style={styles.alertRow}>
                  {sae.alerts.map((a) => (
                    <span key={a} style={styles.alertBadge}>{a}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Alert history */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Alert History</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>SAE ID</th>
              <th style={styles.th}>Patient</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Alert Level</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_SAE_EVENTS.flatMap((sae) =>
              sae.alerts.length > 0
                ? sae.alerts.map((alert) => (
                    <tr key={`${sae.sae_id}-${alert}`}>
                      <td style={styles.td}>{sae.sae_id}</td>
                      <td style={styles.td}>{sae.patient_id}</td>
                      <td style={styles.td}>{sae.report_type}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.alertBadge,
                          backgroundColor: alert === 'overdue' ? '#c53030' : alert === '80_pct' ? '#c05621' : '#b7791f',
                        }}>
                          {alert}
                        </span>
                      </td>
                      <td style={styles.td}>{sae.status}</td>
                    </tr>
                  ))
                : []
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 24, maxWidth: 700 },
  grid: { display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 32 },
  card: { backgroundColor: '#1a202c', borderRadius: 8, padding: 20, minWidth: 280, flex: 1, maxWidth: 400 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  saeId: { color: '#63b3ed', fontWeight: 600, fontSize: 14 },
  patient: { color: '#718096', fontSize: 12, marginTop: 2 },
  typeBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  description: { color: '#a0aec0', fontSize: 12, marginTop: 12 },
  countdown: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16 },
  hoursLeft: { display: 'flex', alignItems: 'baseline', gap: 6 },
  hoursNum: { fontSize: 32, fontWeight: 700 },
  hoursUnit: { color: '#718096', fontSize: 12 },
  statusBadge: { padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  progressBar: { height: 4, backgroundColor: '#2d3748', borderRadius: 2, marginTop: 12, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 2, transition: 'width 0.3s' },
  dates: { display: 'flex', justifyContent: 'space-between', marginTop: 8, color: '#718096', fontSize: 11 },
  alertRow: { display: 'flex', gap: 6, marginTop: 10 },
  alertBadge: { padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: '#fff', backgroundColor: '#c05621' },
  section: { marginTop: 16 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e0', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13 },

  // SUSAR summary
  susarSummaryRow: { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  susarCard: { backgroundColor: '#1a202c', borderRadius: 8, padding: '12px 20px', minWidth: 140, flex: 1, border: '1px solid #2d3748' },
  susarLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 },
  susarValue: { color: '#e2e8f0', fontSize: 28, fontWeight: 700, marginTop: 4 },
};
